/**
 * Resolve LocalizationDictionary keys for preview.
 * Keys are full KeyTranslate values, e.g. `lobby.header.home`.
 */

const DEFAULT_LOCALE = "ru";

/** Known modules from LangModuleEnum / BDUI translationModuleSchema. */
const MODULES = new Set([
  "common",
  "lobby",
  "casino",
  "sportsbook",
  "auth",
  "marketing",
  "wallet",
  "profile",
  "system",
  "telegram",
  "kyc",
  "ai",
  "notifications",
  "unknown",
]);

/** Seed map for draft preview until AdminApp translations are loaded into the viewer. */
const PREVIEW_STRINGS = {
  "lobby.header.home": { en: "Home", ru: "Главная" },
  "lobby.header.casino": { en: "Casino", ru: "Казино" },
  "lobby.header.freemoney": { en: "Free money", ru: "Free money" },
  "lobby.header.betting": { en: "Sport", ru: "Спорт" },
  "lobby.header.login": { en: "Login", ru: "Вход" },
  "lobby.header.register": { en: "Register", ru: "Регистрация" },
  "lobby.nav.lobby": { en: "Lobby", ru: "Лобби" },
  "lobby.nav.live_casino": { en: "Live casino", ru: "Live казино" },
  "lobby.nav.fast_games": { en: "Fast games", ru: "Быстрые игры" },
};

let _locale = DEFAULT_LOCALE;
let _catalog = { ...PREVIEW_STRINGS };

export function setPreviewLocale(locale) {
  if (typeof locale === "string" && locale.trim()) {
    _locale = locale.trim().toLowerCase();
  }
}

/** Merge extra `{ "module.key": { en, ru, ... } }` entries from AdminApp / settings. */
export function mergePreviewTranslations(entries) {
  if (!entries || typeof entries !== "object") return;
  for (const [key, locales] of Object.entries(entries)) {
    if (!key || !locales || typeof locales !== "object") continue;
    _catalog[key] = { ...(_catalog[key] || {}), ...locales };
  }
}

function dictionaryKeyFromParts(module, key) {
  const mod = String(module || "").toLowerCase();
  const section = String(key || "").trim();
  if (!mod || !section) return "";
  if (section.startsWith(`${mod}.`)) return section;
  return `${mod}.${section}`;
}

/** Full KeyTranslate like `lobby.header.home`. */
export function isDictionaryKey(value) {
  if (typeof value !== "string") return false;
  const key = value.trim();
  if (!/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/.test(key)) return false;
  const root = key.split(".")[0];
  return MODULES.has(root);
}

function lookupKey(fullKey, locale = _locale) {
  const locales = _catalog[fullKey];
  if (!locales || typeof locales !== "object") return null;
  const lang = String(locale || DEFAULT_LOCALE).toLowerCase();
  return (
    locales[lang]
    || locales[lang.split("-")[0]]
    || locales.ru
    || locales.en
    || locales.RU
    || locales.EN
    || null
  );
}

/**
 * @param {unknown} value full key string | { module, key, params? } | literal label
 * @param {string} [locale]
 * @returns {string}
 */
export function resolveLocalizedText(value, locale = _locale) {
  if (value == null) return "";

  if (typeof value === "string") {
    const key = value.trim();
    if (isDictionaryKey(key)) {
      return lookupKey(key, locale) ?? key;
    }
    return value;
  }

  if (typeof value !== "object") return String(value);

  const module = value.module;
  const key = value.key;
  if (typeof module !== "string" || typeof key !== "string") return "";

  const fullKey = dictionaryKeyFromParts(module, key);
  return lookupKey(fullKey, locale) ?? lookupKey(key, locale) ?? (fullKey || key);
}

/**
 * Nav / chrome label: English `title` + optional LocalizationDictionary `title_key`.
 * Never treat `title` as a dictionary key.
 * @param {{ title?: unknown, title_key?: unknown }} fields
 * @param {string} [locale]
 * @returns {string}
 */
export function resolveTitleFields(fields, locale = _locale) {
  if (!fields || typeof fields !== "object") return "";
  const title = typeof fields.title === "string" ? fields.title : "";
  const titleKey = typeof fields.title_key === "string" ? fields.title_key.trim() : "";
  if (titleKey) {
    return lookupKey(titleKey, locale) || title || titleKey;
  }
  return title;
}

/** @deprecated Prefer full-key strings; kept for older draft shapes. */
export function isLocalizedTextRef(value) {
  return Boolean(
    value
    && typeof value === "object"
    && !Array.isArray(value)
    && typeof value.module === "string"
    && typeof value.key === "string",
  );
}
